import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Grid, Box, Menu, CircularProgress, ClickAwayListener, Portal } from '@mui/material';

import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ApiIcon from '@mui/icons-material/Api';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { COLOR_PRIMARY_DARK, COLOR_BLACK } from 'src/const';
import { KPI_CARD_COLOR } from 'src/colors';
import { setLeadFormSource } from 'src/store/Dashboard/actions';
import NestedMenuItem from 'src/App/Components/NestedMenuItem';

const MenuIconStyle = {
  color: COLOR_BLACK,
  paddingRight: '5px',
};

const ChatDrawer = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const tawkURL = 'https://tawk.to/chat/5f42ecee1e7ade5df4434438/default';

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Portal container={document.getElementById('root')}>
        <Box
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: { xs: '0px', sm: '20px' },
            width: 'clamp(280px, 100%, 400px)',
            height: { xs: '90vh', sm: '80vh' },
            zIndex: 9999,
            borderRadius: '20px',
            // overflow: 'hidden',
            boxShadow: '0 4px 20px #0002',
            border: '2px solid white',
            backgroundColor: '#49b29f',
            // paddingTop: '20px',
            '& iframe': {
              height: 'calc(100% - 4px)',
              border: 'none',
              outline: 'none',
              width: '100%',
              position: 'relative',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '-32px',
              right: 0,
              cursor: 'pointer',
              backgroundColor: '#49b29f',
              borderRadius: '900px',
              padding: '10px',
              fontWeight: 'bold',
              fontSize: '14px',
              zIndex: 99998,
              border: '2px solid #fff3'
            }}
            onClick={onClose}
          >
            <CloseRoundedIcon sx={{ color: '#fff' }} />
          </Box>
          <Box sx={{ overflow: 'hidden', borderRadius: '20px', height: 'calc(100% - 0px)', zIndex: 99999, }}>          
          <Box
            sx={{
              position: 'relative',
              top: '2px',
              height: '2px',
              zIndex: 9998,
              backgroundColor: '#49b29f',
            }}
          />
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress size={60} sx={{ color: '#fff' }} />
            </Box>
          )}
          <iframe src={tawkURL} onLoad={() => setIsLoading(false)} />
          </Box>
        </Box>
      </Portal>
    </ClickAwayListener>
  );
};
const HelpMenu = ({
  handleMarketSidebarClick,
  handleSidebarListItemClick,
  openDemoBookingForm,
  setDemoFormHeadingFor,
}) => {
  const dispatch = useDispatch();

  const [helperEl, setHelperEl] = useState(null);
  const [enterpriseDropDownOpen, setEnterpriseDropDownOpen] = useState(false);

  const [showChat, setShowChat] = useState(false);

  const handleHelperClick = ({ currentTarget }) => {
    setHelperEl(currentTarget);
  };
  const handleHelperClose = () => {
    setHelperEl(null);
  };

  const handleEnterpriseDropdownClick = () => {
    setEnterpriseDropDownOpen(!enterpriseDropDownOpen);
  };

  const handleCustomDataBtnClick = () => {
    handleHelperClose();
    setEnterpriseDropDownOpen(false);
    setDemoFormHeadingFor('sales');
    openDemoBookingForm();
    dispatch(setLeadFormSource('app-header'));
  };

  const handleApiBtnClick = () => {
    setEnterpriseDropDownOpen(false);
    window.open('https://documenter.getpostman.com/view/25155751/2s9XxtyvNn', '_blank');
  };

  const openChat = () => {
    setShowChat(true);
    handleHelperClose();
  };

  const opeFreeWebinar = () => {
    window.open('https://airbticsltd.ewebinar.com/webinar/airbtics-onboarding-16533');
  };

  const openFAQ = () => {
    window.open('https://support.airbtics.com/');
  };

  const helpMenuItems = [
    {
      title: 'Free Webinar',
      handleClick: opeFreeWebinar,
      icon: <OpenInNewRoundedIcon sx={MenuIconStyle} />,
    },
    {
      title: 'Support',
      handleClick: openChat,
      icon: <SupportAgentIcon sx={MenuIconStyle} />,
    },
    {
      title: 'FAQ',
      handleClick: openFAQ,
      icon: <QuestionAnswerIcon sx={MenuIconStyle} />,
    },
    {
      title: 'Enterprise',
      handleClick: handleEnterpriseDropdownClick,
      icon: <BusinessRoundedIcon sx={MenuIconStyle} />,
      open: enterpriseDropDownOpen,
      children: [
        {
          title: 'Custom Data',
          handleClick: handleCustomDataBtnClick,
          icon: <DataObjectIcon sx={MenuIconStyle} />,
        },
        {
          title: 'API',
          handleClick: handleApiBtnClick,
          icon: <ApiIcon sx={MenuIconStyle} />,
        },
      ],
    },
  ];

  return (
    <>
      <Grid item>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 200,
            border: `4px solid ${COLOR_PRIMARY_DARK}`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <QuestionMarkRoundedIcon
            sx={{
              width: 30,
              height: 30,
              cursor: 'pointer',
              color: KPI_CARD_COLOR,
            }}
            onClick={handleHelperClick}
          />
        </Box>

        <Menu
          anchorEl={helperEl}
          open={Boolean(helperEl)}
          onClose={handleHelperClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '10px',
            },
          }}
        >
          {helpMenuItems.map(item => (
            <NestedMenuItem
              title={item.title}
              handleClick={item.handleClick}
              icon={item.icon}
              open={item.open}
              children={item.children}
            />
          ))}
        </Menu>
      </Grid>
      {showChat && <ChatDrawer onClose={() => setShowChat(false)} />}
    </>
  );
};

export default HelpMenu;
